import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input
} from '@angular/core';
import { UiQuery } from '../../state/ui/ui.query';
import numeral from 'numeral';
import { of, OperatorFunction, ReplaySubject } from 'rxjs';
import { concatMap, delay } from 'rxjs/operators';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { CommonModule } from '@angular/common';
import { FormatCurrencyPipe } from '../../pipes/format-currency.pipe';
import { FormattedNumberPipe } from '../../pipes/formatted-number.pipe';
import { FormattedPercentPipe } from '../../pipes/formatted-percent.pipe';

interface MetricValue {
    '--fraction': number | string;
    '--leading-zeros': number | string;
    '--integer': number;
    '--suffix': string;
    '--prefix': string;
    '--sign': string;
}

export interface SbKeyMetric {
    value: number | string;
    type: 'number' | 'percent' | 'date' | 'string' | 'integer';
    class?: string;
    format?: string;
    prefix?: string;
    suffix?: string;
    label?: string;
    visible?: boolean;
    order?: number;
    ccy?: string;
    excludeEmpty?: boolean; // Exclude metric value from presentation if not truthy
    style?: MetricValue;
}

export interface SbKeyMetricTable {
    property?: string;
    inception?: string;
    value?: number;
    valueLocalCcy?: number;
    rentalIncome?: number;
    totalArea?: number;
    totalEconomicArea?: number;
    occupiedArea?: number;
    ownerhipValue?: number;
    loanToValue?: number;
    economicOccupancyRate?: number;
    lastUpdated?: string;
}

export function preMapOnce<T>(tapFn: (t: T) => T, mapIndex = 0): OperatorFunction<T, T> {
    return (source$) =>
        source$.pipe(
            concatMap((value, index) => {
                if (index === mapIndex) {
                    return of(tapFn(value), value).pipe(
                        concatMap((value) => of(value).pipe(delay(0)))
                    );
                }
                return of(value);
            })
        );
}
/** @deprecated - This component should not be used, please refer to `sb-key-metrics.component.ts` instead */
@Component({
    selector: 'sb-key-metric-list',
    templateUrl: './sb-key-metric-list.component.html',
    styleUrls: ['./sb-key-metric-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatTooltipModule,
        FormatCurrencyPipe,
        FormattedNumberPipe,
        FormattedPercentPipe
    ],
    standalone: true
})
export class SbKeyMetricListComponent {
    constructor(private _cdr: ChangeDetectorRef, private uiQuery: UiQuery) {}
    @Input() set metrics(metrics: SbKeyMetric[]) {
        if (metrics) {
            this._metrics$.next(
                metrics?.map((metric) => ({
                    ...metric,
                    style: this.displayValue(metric)
                }))
            );
        }
    }
    public cssPropertySupport = this.uiQuery.getValue().cssPropertySupport;
    private _metrics$ = new ReplaySubject<Array<SbKeyMetric>>(1);
    metrics$ = this._metrics$.pipe(
        preMapOnce((metrics) =>
            metrics.map((metric) => ({
                ...metric,
                style: this.displayValue(metric, true)
            }))
        )
    );
    displayValue(
        { value, format, prefix, suffix, ccy }: Partial<SbKeyMetric>,
        initialValue?: boolean
    ) {
        if (initialValue) {
            value = 0;
        }
        const prettyValue = format ? numeral(value).format(format) : value + '';
        const {
            groups: { sign, integer, fraction, abbreviation }
        } = prettyValue.match(
            /(?<sign>-)?(?<integer>\d*).?(?<fraction>\d*)(?<abbreviation>.*)/
        );
        const fractionInteger = +fraction.toString().replace(/\b[0.]*/, '');
        const leadingZeros = prettyValue?.startsWith('-')
            ? ''
            : Number.isInteger(fraction)
            ? 0
            : (fraction + '').length - (fractionInteger + '').length;
        if (abbreviation) {
            suffix = suffix ? `${abbreviation}${suffix}` : abbreviation;
        }
        if (ccy) {
            suffix = suffix ? `${suffix} ${ccy}` : ` ${ccy}`;
        }
        return {
            '--integer': +integer,
            '--fraction': +fraction.slice(0, fraction.length < 9 ? fraction.length : 9),
            '--leading-zeros': leadingZeros,
            '--sign': "'" + (sign || '') + "'",
            '--prefix': "'" + (prefix || '') + "'",
            '--suffix': "'" + (suffix || '') + "'",
            '--fallback': "'" + prettyValue + "'"
        };
    }

    metricLabel(index: number, metric: SbKeyMetric) {
        return metric.label;
    }
}
